/**
 * Bộ tải lịch của màn Lịch (đợt 3 · 3.1) — phần điều khiển thuần, DayBoard.tsx chỉ nối vào React.
 *
 * Bốn lời hứa (booking-logic-auditor soát "không đường nào hiện lịch cũ như mới"):
 *   1. Chi nhánh lỗi → tự thử lại (2s, 5s, 15s, rồi mỗi 30s), không để bảng đứng.
 *   2. Lịch lỗi → GIỮ NGUYÊN số đang xem (không xoá, không nháy trắng), chỉ báo lỗi và hẹn thử lại. Số đang xem
 *      luôn kèm giờ của lần tải thành công gần nhất (`lichXong(d, luc)`), nên không ai đọc số cũ như số mới.
 *   3. Chỉ nhận kết quả của lần tải MỚI NHẤT (`lanMoi`, cùng cơ chế `useLanTaiMoiNhat`) VÀ chỉ khi (chi nhánh, ngày)
 *      của lần gọi vẫn là ĐÍCH hiện tại của màn (`dich()`): phản hồi chậm của lần cũ không đè bản mới hơn, và hàm
 *      của lần vẽ cũ (closure của `doiLich`, vòng thử lại) không bao giờ kéo lịch ngày / chi nhánh khác vào bảng
 *      kèm "Cập nhật lúc" mới (B1 booking-logic-auditor soát 780fd94). `nap()` không có tham số: đích do màn giữ.
 *   4. KHÔNG lưu gì ra bộ nhớ máy (NOT-DOING #12). Không có đường nào đọc lịch từ nơi nào khác ngoài máy chủ.
 */
import { type Api, ApiFailure, type Branch, type DayBoard, type ServiceOption } from './api.ts'
import { T } from './text/vi.ts'
import { type Hen, henThat, nenThuLai, taoBoThuLai } from './tu-phuc-hoi.ts'

/** Điều màn hình cần nói với người dùng khi việc tải không xong. */
export interface LoiTai {
  /** Mất mạng (hết giờ / rớt): hiện "Không có mạng · đang thử lại…". */
  matMang: boolean
  /** Câu máy chủ nói (hoặc câu chung) khi KHÔNG phải mất mạng. */
  thongBao: string
  /** Máy đang tự thử lại. `false` = lỗi nghiệp vụ, chờ người bấm "Tải lại". */
  dangThuLai: boolean
}

export interface PhanUng {
  chiNhanhXong(branches: Branch[], services: ServiceOption[]): void
  /** `luc` = "hh:mm" giờ Việt Nam của lần tải thành công này. */
  lichXong(lich: DayBoard, luc: string): void
  /** `null` = đã về, gỡ dải báo. */
  loi(loi: LoiTai | null): void
}

/** Chi nhánh + ngày màn đang xem. */
export interface Dich {
  branchId: string
  date: string
}

export interface TuyChon {
  api: Pick<Api, 'branches' | 'services' | 'day'>
  /** Đích HIỆN TẠI của màn (đọc lại mỗi lần tải và lúc ghi) — màn giữ trong một ref, không truyền qua closure. */
  dich: () => Dich
  /** Có tải danh sách dịch vụ cùng chi nhánh không (chỉ vai đặt lịch / nhận khách). */
  canDichVu: boolean
  /** `useLanTaiMoiNhat()`: mỗi lần gọi là một lần tải mới, hàm trả về cho biết "còn là mới nhất không". */
  lanMoi: () => () => boolean
  phanUng: PhanUng
  /** Giờ "hh:mm" giờ VN của lúc này. */
  gio: () => string
  hen?: Hen
  /** 0..1, rải ±20% bậc thử lại. Thiếu = đúng bậc (xem `taoBoThuLai`). */
  ngauNhien?: () => number
}

export interface BoTaiLich {
  taiChiNhanh(): Promise<void>
  /** Tải lịch của đích hiện tại. */
  nap(): Promise<void>
  /**
   * Tải lại do máy tự động (có mạng trở lại, tab hiện lại, 60 giây một lần): chưa có chi nhánh thì tải chi nhánh,
   * có rồi thì tải lịch. Không động tới tờ đang mở, không xoá số đang xem.
   */
  taiLaiTuDong(): Promise<void>
  chiNhanhDaCo(): boolean
  /** Màn đóng: bỏ mọi hẹn, bỏ qua kết quả về muộn. */
  huy(): void
}

export function taoBoTaiLich(o: TuyChon): BoTaiLich {
  const hen = o.hen ?? henThat
  const thuChiNhanh = taoBoThuLai(hen, o.ngauNhien)
  const thuLich = taoBoThuLai(hen, o.ngauNhien)
  let daHuy = false
  let coChiNhanh = false

  function baoLoi(e: unknown, bo: typeof thuChiNhanh, thuLai: () => void): void {
    const f = e instanceof ApiFailure ? e : null
    const dangThuLai = f !== null && nenThuLai(f.status)
    if (dangThuLai) bo.henLai(thuLai)
    o.phanUng.loi({
      matMang: f?.code === 'OFFLINE',
      thongBao: f?.messageVi ?? T.serverFailed,
      dangThuLai,
    })
  }

  async function taiChiNhanh(): Promise<void> {
    if (daHuy) return
    thuChiNhanh.huyHen()
    try {
      const [b, s] = await Promise.all([
        o.api.branches(),
        o.canDichVu ? o.api.services() : Promise.resolve({ services: [] as ServiceOption[] }),
      ])
      if (daHuy) return
      coChiNhanh = true
      thuChiNhanh.datLai()
      o.phanUng.chiNhanhXong(b.branches, s.services)
      o.phanUng.loi(null)
    } catch (e) {
      if (daHuy) return
      baoLoi(e, thuChiNhanh, () => void taiChiNhanh())
    }
  }

  async function nap(): Promise<void> {
    if (daHuy) return
    const d = o.dich()
    if (d.branchId === '') return
    thuLich.huyHen()
    const conMoi = o.lanMoi()
    /** Còn là đích của màn không? Đổi ngày / chi nhánh giữa chừng thì kết quả này là của màn khác. */
    const dungDich = () => {
      const c = o.dich()
      return c.branchId === d.branchId && c.date === d.date
    }
    try {
      const kq = await o.api.day(d.branchId, d.date)
      if (daHuy || !conMoi() || !dungDich()) return
      thuLich.datLai()
      o.phanUng.lichXong(kq, o.gio())
      o.phanUng.loi(null)
    } catch (e) {
      if (daHuy || !conMoi() || !dungDich()) return
      // Hẹn thử lại gọi `nap()` KHÔNG đối số: đọc đích mới nhất lúc thử, không giữ đích của lúc lỗi.
      baoLoi(e, thuLich, () => void nap())
    }
  }

  return {
    taiChiNhanh,
    nap,
    taiLaiTuDong: () => (coChiNhanh ? nap() : taiChiNhanh()),
    chiNhanhDaCo: () => coChiNhanh,
    huy() {
      daHuy = true
      thuChiNhanh.huyHen()
      thuLich.huyHen()
    },
  }
}
